import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { downloadReportExcel, buildReportExcelFilename } from '../../../../core/utils/report-excel-export.util';
import { ApiParkingCardListService } from '../../../../core/services/api/parking/api-parking-card-list.service';
import { ApiRegisterMemberPaymentReportService } from '../../../../core/services/api/report/api-register-member-payment-report.service';
import { MapperRegisterMemberPaymentService } from '../../../../core/mappers/report/mapper-register-member-payment.service';
import { RegisterMemberPaymentTableResult } from '../../../../core/models/report/register-member-payment.model';
import { IdNameOption } from '../../../../core/models/parking/parking-card-list.model';
import { ParkingVehicleTypeResponse } from '../../../../core/models/vehicle.model';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalInvoicePdfViewerComponent } from '../../modals/finance/invoice/modal-invoice-pdf-viewer/modal-invoice-pdf-viewer.component';

@Component({
  selector: 'app-register-member-payment-report',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalInvoicePdfViewerComponent],
  templateUrl: './register-member-payment-report.component.html',
  styleUrl: './register-member-payment-report.component.css',
})
export class RegisterMemberPaymentReportComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly cardApi = inject(ApiParkingCardListService);
  private readonly reportApi = inject(ApiRegisterMemberPaymentReportService);
  private readonly mapper = inject(MapperRegisterMemberPaymentService);
  private readonly toast = inject(ToastService);

  subscriptions = signal<IdNameOption[]>([]);
  vehicleTypes = signal<ParkingVehicleTypeResponse[]>([]);
  isLoadingSubs = signal(false);
  isLoadingVehicleTypes = signal(false);
  isLoading = signal(false);
  isExporting = signal(false);
  isProcessingPdf = signal(false);
  showPdfModal = signal(false);
  hasSearched = signal(false);
  pdfSrc: Uint8Array | null = null;
  pdfDownloadName = 'register-member-payment.pdf';
  lastFetchedTime: Date | null = null;

  subscriptionId: number | null = null;
  searchSubscriptionText = '';
  isSubscriptionDropdownOpen = false;

  vehicleTypeId: number | null = null;
  searchVehicleTypeText = '';
  isVehicleTypeDropdownOpen = false;

  /** yyyy-MM-dd */
  startDate = '';
  endDate = '';
  private readonly maxRangeMonths = 2;

  result = signal<RegisterMemberPaymentTableResult | null>(null);

  constructor() {
    this.applyDefaultMonthDates();
    effect(() => {
      const siteId = this.siteState.selectedSite()?.id;
      untracked(() => {
        this.subscriptionId = null;
        this.searchSubscriptionText = '';
        this.clearResult();
        if (siteId) this.loadSubscriptions(siteId);
        else this.subscriptions.set([]);
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get selectedSubscriptionName(): string {
    if (!this.subscriptionId) return 'ทุกแพ็กเกจ';
    return this.subscriptions().find((s) => s.id === this.subscriptionId)?.name ?? '';
  }

  get filteredSubscriptions(): IdNameOption[] {
    const q = this.searchSubscriptionText.trim().toLowerCase();
    const list = this.subscriptions();
    if (!q || q === 'ทุกแพ็กเกจ') return list;
    return list.filter((s) => (s.name || '').toLowerCase().includes(q));
  }

  get selectedVehicleTypeName(): string {
    if (!this.vehicleTypeId) return 'ทุกประเภท';
    return this.vehicleTypes().find((v) => v.id === this.vehicleTypeId)?.typeName ?? '';
  }

  get filteredVehicleTypes(): ParkingVehicleTypeResponse[] {
    const q = this.searchVehicleTypeText.trim().toLowerCase();
    const list = this.vehicleTypes();
    if (!q || q === 'ทุกประเภท') return list;
    return list.filter((v) => (v.typeName || '').toLowerCase().includes(q));
  }

  get canSearch(): boolean {
    return this.selectedSiteId > 0 && !!this.startDate && !!this.endDate;
  }

  get groups() {
    return this.result()?.groups ?? [];
  }

  get hasData(): boolean {
    return this.groups.some((g) => g.rows.length > 0);
  }

  get totals() {
    return this.result()?.totals ?? null;
  }

  get maxEndDate(): string | undefined {
    if (!this.startDate) return undefined;
    const start = this.parseInputDate(this.startDate);
    if (!start) return undefined;
    return this.toApiDate(
      new Date(start.getFullYear(), start.getMonth() + this.maxRangeMonths, start.getDate()),
    );
  }

  ngOnInit(): void {
    this.ensureDates();
    this.loadVehicleTypes();
    if (this.selectedSiteId) this.loadSubscriptions(this.selectedSiteId);
  }

  onFilterChange(): void {
    this.ensureDates();
    if (this.startDate && this.endDate && this.isRangeOverMax(this.startDate, this.endDate)) {
      const maxEnd = this.maxEndDate;
      if (maxEnd) this.endDate = maxEnd;
      this.toast.warn('เรียกดูได้สูงสุด 2 เดือน — ปรับวันสิ้นสุดให้อัตโนมัติ');
    }
    this.clearResult();
  }

  closeDropdowns(): void {
    if (this.isSubscriptionDropdownOpen) {
      const text = this.searchSubscriptionText.trim().toLowerCase();
      if (!text || text === 'ทุกแพ็กเกจ') {
        this.clearSubscription();
      } else {
        const match = this.subscriptions().find(
          (s) => (s.name || '').toLowerCase() === text,
        );
        if (match) {
          this.subscriptionId = match.id;
          this.searchSubscriptionText = match.name;
        } else if (this.subscriptionId) {
          this.searchSubscriptionText = this.selectedSubscriptionName;
        } else {
          this.searchSubscriptionText = '';
        }
      }
    }
    this.isSubscriptionDropdownOpen = false;

    if (this.isVehicleTypeDropdownOpen) {
      const text = this.searchVehicleTypeText.trim().toLowerCase();
      if (!text || text === 'ทุกประเภท') {
        this.clearVehicleType();
      } else {
        const match = this.vehicleTypes().find(
          (v) => (v.typeName || '').toLowerCase() === text,
        );
        if (match) {
          this.vehicleTypeId = match.id;
          this.searchVehicleTypeText = match.typeName;
        } else if (this.vehicleTypeId) {
          this.searchVehicleTypeText = this.selectedVehicleTypeName;
        } else {
          this.searchVehicleTypeText = '';
        }
      }
    }
    this.isVehicleTypeDropdownOpen = false;
  }

  selectSubscription(opt: IdNameOption): void {
    this.subscriptionId = opt.id;
    this.searchSubscriptionText = opt.name;
    this.isSubscriptionDropdownOpen = false;
    this.clearResult();
  }

  clearSubscription(): void {
    this.subscriptionId = null;
    this.searchSubscriptionText = '';
    this.isSubscriptionDropdownOpen = false;
    this.clearResult();
  }

  selectVehicleType(opt: ParkingVehicleTypeResponse): void {
    this.vehicleTypeId = opt.id;
    this.searchVehicleTypeText = opt.typeName;
    this.isVehicleTypeDropdownOpen = false;
    this.clearResult();
  }

  clearVehicleType(): void {
    this.vehicleTypeId = null;
    this.searchVehicleTypeText = '';
    this.isVehicleTypeDropdownOpen = false;
    this.clearResult();
  }

  onSearch(): void {
    this.ensureDates();
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจาก Navbar');
      return;
    }
    if (!this.startDate || !this.endDate) {
      this.toast.error('กรุณาเลือกช่วงวันที่');
      return;
    }
    if (this.startDate > this.endDate) {
      this.toast.error('วันสิ้นสุดต้องไม่ก่อนวันเริ่มต้น');
      return;
    }
    if (this.isRangeOverMax(this.startDate, this.endDate)) {
      this.toast.error('เรียกดูรายงานได้สูงสุด 2 เดือนเท่านั้น');
      return;
    }
    this.loadTable();
  }

  onRefresh(): void {
    if (!this.hasSearched()) {
      this.onSearch();
      return;
    }
    this.loadTable();
  }

  loadTable(): void {
    if (!this.canSearch) {
      this.clearResult();
      return;
    }

    this.isLoading.set(true);
    this.reportApi
      .getRegisterMemberPaymentForTable({
        siteId: this.selectedSiteId,
        subscriptionId: this.subscriptionId,
        vehicleTypeId: this.vehicleTypeId,
        startDate: this.startDate,
        endDate: this.endDate,
      })
      .subscribe({
        next: (res) => {
          this.result.set(this.mapper.mapTable(res));
          this.hasSearched.set(true);
          this.lastFetchedTime = new Date();
          this.isLoading.set(false);
        },
        error: (err) => {
          this.result.set(null);
          this.hasSearched.set(true);
          this.isLoading.set(false);
          this.toast.error(err?.error?.message || 'โหลดรายงานไม่สำเร็จ');
        },
      });
  }

  async onExportExcel(): Promise<void> {
    if (!this.result() || !this.hasData) {
      this.toast.error('ไม่มีข้อมูลให้ส่งออก');
      return;
    }

    this.isExporting.set(true);
    try {
      const exportRows: Record<string, string | number>[] = [];

      for (const g of this.groups) {
        exportRows.push({
          'ประเภทรถ': g.vehicleTypeName,
          'ลำดับ': `รวม ${g.rowCount} รายการ`,
          'ทะเบียน': '',
          'ประเภทชำระ': '',
          'ค่าธรรมเนียม': g.sumFee,
          'ประเภทสมัคร': '',
          'แพ็กเกจ': '',
          'วันที่ชำระ': '',
          'เริ่มสมาชิก': '',
          'สิ้นสุดสมาชิก': '',
          'เลขคำขอ': '',
        });

        for (const r of g.rows) {
          exportRows.push({
            'ประเภทรถ': g.vehicleTypeName,
            'ลำดับ': r.seq,
            'ทะเบียน': r.license,
            'ประเภทชำระ': r.feeTypeName,
            'ค่าธรรมเนียม': r.fee,
            'ประเภทสมัคร': r.registerMemberType,
            'แพ็กเกจ': r.subscriptionName,
            'วันที่ชำระ': this.formatDateTime(r.paymentDate),
            'เริ่มสมาชิก': this.formatDateTime(r.memberStart),
            'สิ้นสุดสมาชิก': this.formatDateTime(r.memberEnd),
            'เลขคำขอ': r.requestNo,
          });
        }
      }

      const t = this.totals!;
      exportRows.push({
        'ประเภทรถ': 'รวมทั้งหมด',
        'ลำดับ': t.rowCount,
        'ทะเบียน': '',
        'ประเภทชำระ': '',
        'ค่าธรรมเนียม': t.sumFee,
        'ประเภทสมัคร': '',
        'แพ็กเกจ': '',
        'วันที่ชำระ': '',
        'เริ่มสมาชิก': '',
        'สิ้นสุดสมาชิก': '',
        'เลขคำขอ': '',
      });

      const siteName = this.result()?.siteName || this.siteState.selectedSite()?.name || '—';
      const extraLines: string[] = [];
      if (this.subscriptionId) {
        extraLines.push(`แพ็กเกจ: ${this.selectedSubscriptionName || '—'}`);
      }
      if (this.vehicleTypeId) {
        extraLines.push(`ประเภทรถ: ${this.selectedVehicleTypeName || '—'}`);
      }
      await downloadReportExcel(exportRows, {
        reportTitle: 'รายงานชำระค่าสมัครสมาชิก (Register Member Payment)',
        siteName,
        siteAddress: this.result()?.siteAddress,
        siteTaxId: this.result()?.siteTaxId,
        startDate: this.startDate,
        endDate: this.endDate,
        extraLines,
        sheetName: 'RegisterMemberPayment',
        fileName: buildReportExcelFilename('register-member-payment', siteName, this.startDate, this.endDate),
      });
      this.toast.success('ส่งออก Excel สำเร็จ');
    } catch {
      this.toast.error('ส่งออก Excel ไม่สำเร็จ');
    } finally {
      this.isExporting.set(false);
    }
  }

  onPrintPdf(): void {
    if (!this.canSearch) {
      this.toast.error('กรุณาเลือกลานและช่วงวันที่');
      return;
    }
    if (this.isRangeOverMax(this.startDate, this.endDate)) {
      this.toast.error('เรียกดูรายงานได้สูงสุด 2 เดือนเท่านั้น');
      return;
    }

    this.isProcessingPdf.set(true);
    const siteName = (this.siteState.selectedSite()?.name || 'site').replace(/\s+/g, '_');
    const subName = (this.subscriptionId ? this.selectedSubscriptionName : 'all').replace(/\s+/g, '_');
    const vtName = (this.vehicleTypeId ? this.selectedVehicleTypeName : 'all').replace(/\s+/g, '_');
    this.pdfDownloadName = `register-member-payment_${siteName}_${subName}_${vtName}_${this.startDate}_${this.endDate}.pdf`;

    this.reportApi
      .getRegisterMemberPaymentPdf({
        siteId: this.selectedSiteId,
        subscriptionId: this.subscriptionId,
        vehicleTypeId: this.vehicleTypeId,
        startDate: this.startDate,
        endDate: this.endDate,
      })
      .subscribe({
        next: (bytes) => {
          this.pdfSrc = bytes;
          this.showPdfModal.set(true);
          this.isProcessingPdf.set(false);
        },
        error: (err) => {
          console.error('[API Error] getRegisterMemberPaymentPdf:', err);
          this.toast.error('ไม่สามารถดึงไฟล์ PDF ได้');
          this.isProcessingPdf.set(false);
        },
      });
  }

  closePdfModal(): void {
    this.showPdfModal.set(false);
    this.pdfSrc = null;
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  }

  private clearResult(): void {
    this.result.set(null);
    this.hasSearched.set(false);
    this.lastFetchedTime = null;
  }

  private toApiDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private applyDefaultMonthDates(): void {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    this.startDate = this.toApiDate(start);
    this.endDate = this.toApiDate(end);
  }

  private ensureDates(): void {
    if (!this.startDate?.trim() || !this.endDate?.trim()) {
      this.applyDefaultMonthDates();
      return;
    }
    if (this.startDate > this.endDate) this.endDate = this.startDate;
  }

  private parseInputDate(value: string): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private isRangeOverMax(startStr: string, endStr: string): boolean {
    const start = this.parseInputDate(startStr);
    const end = this.parseInputDate(endStr);
    if (!start || !end) return false;
    const maxEnd = new Date(start.getFullYear(), start.getMonth() + this.maxRangeMonths, start.getDate());
    return end > maxEnd;
  }

  private loadSubscriptions(siteId: number): void {
    this.isLoadingSubs.set(true);
    this.cardApi.getAllParkingSubscriptionsBySite(siteId).subscribe({
      next: (list) => {
        const mapped = (list || []).map((x: any) => ({
          id: Number(x.id ?? x.Id ?? 0),
          name: String(x.name ?? x.Name ?? ''),
        }));
        this.subscriptions.set(mapped.filter((x) => x.id > 0));
        this.isLoadingSubs.set(false);
      },
      error: () => {
        this.subscriptions.set([]);
        this.isLoadingSubs.set(false);
        this.toast.error('โหลดแพ็กเกจสมาชิกไม่สำเร็จ');
      },
    });
  }

  private loadVehicleTypes(): void {
    this.isLoadingVehicleTypes.set(true);
    this.cardApi.getParkingVehicleTypes().subscribe({
      next: (res) => {
        const mapped = (res ?? []).map((row) => {
          const raw = row as ParkingVehicleTypeResponse & Record<string, unknown>;
          return {
            id: Number(row.id ?? raw['Id'] ?? 0),
            typeName: String(row.typeName ?? raw['TypeName'] ?? ''),
            icon: String(row.icon ?? raw['Icon'] ?? ''),
          };
        });
        this.vehicleTypes.set(mapped.filter((x) => x.id > 0));
        this.isLoadingVehicleTypes.set(false);
      },
      error: () => {
        this.vehicleTypes.set([]);
        this.isLoadingVehicleTypes.set(false);
        this.toast.error('โหลดประเภทรถไม่สำเร็จ');
      },
    });
  }
}
